<template>
    <v-card
        class="roteirizacao-progresso-destinos"
        elevation="8"
        role="status"
        aria-live="polite"
        :aria-label="title || $t('routePlanning.multiDestination.progress.title')"
    >
        <div class="roteirizacao-progresso-destinos__header">
            <button
                type="button"
                class="roteirizacao-progresso-destinos__toggle"
                :aria-expanded="String(!collapsed)"
                @click="$emit('update:collapsed', !collapsed)"
            >
                <v-progress-circular
                    v-if="running"
                    indeterminate
                    size="16"
                    width="2"
                    color="primary"
                    class="mr-2"
                />
                <v-icon v-else small :color="overall.done === overall.total ? 'success' : 'warning'" class="mr-2">
                    {{ overall.done === overall.total ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                </v-icon>
                <span class="roteirizacao-progresso-destinos__title">
                    {{ title || (single
                        ? $t('routePlanning.multiDestination.progress.titleSingle')
                        : $t('routePlanning.multiDestination.progress.title')) }}
                </span>
                <span class="roteirizacao-progresso-destinos__summary">
                    <template v-if="summaryText">{{ summaryText }}{{ overall.indeterminate ? '' : ' ·' }}</template>
                    <template v-else-if="!single">
                        {{ $t('routePlanning.multiDestination.progress.summary', { done: overall.done, total: overall.total }) }}
                        ·
                    </template>
                    <template v-if="!overall.indeterminate">{{ overall.percent }}%</template>
                </span>
                <v-icon small class="ml-auto">{{ collapsed ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            </button>
            <v-btn
                v-if="!running"
                icon
                x-small
                :aria-label="$t('routePlanning.multiDestination.progress.close')"
                @click="$emit('close')"
            >
                <v-icon x-small>mdi-close</v-icon>
            </v-btn>
        </div>
        <v-progress-linear
            :value="overall.percent"
            :indeterminate="!!overall.indeterminate"
            height="4"
            :color="running ? 'primary' : 'success'"
        />
        <div v-show="!collapsed" class="roteirizacao-progresso-destinos__body">
            <div
                v-for="item in items"
                :key="item.key"
                class="roteirizacao-progresso-destinos__row"
                :class="{
                    'roteirizacao-progresso-destinos__row--active': item.active,
                    'roteirizacao-progresso-destinos__row--clickable': !item.active,
                }"
                :tabindex="item.active ? -1 : 0"
                @click="!item.active && $emit('select', item.key)"
                @keydown.enter="!item.active && $emit('select', item.key)"
            >
                <v-icon small :color="item.statusColor">{{ item.statusIcon }}</v-icon>
                <div class="roteirizacao-progresso-destinos__info">
                    <div class="roteirizacao-progresso-destinos__name">
                        {{ item.label }}
                        <span v-if="item.active && !single" class="roteirizacao-progresso-destinos__badge">
                            {{ $t('routePlanning.multiDestination.progress.viewing') }}
                        </span>
                    </div>
                    <div class="roteirizacao-progresso-destinos__message" :class="{ 'error--text': item.status === 'error' }">
                        {{ item.message }}
                    </div>
                    <v-progress-linear
                        v-if="item.status === 'running' || item.status === 'queued' || item.busy"
                        :value="item.progress"
                        :indeterminate="item.status === 'queued' || !!item.indeterminate"
                        height="3"
                        class="mt-1"
                    />
                </div>
                <span v-if="(item.status === 'running' || item.busy) && !item.indeterminate" class="roteirizacao-progresso-destinos__pct">
                    {{ Math.round(item.progress) }}%
                </span>
                <v-btn
                    v-if="item.retryable"
                    x-small
                    text
                    color="primary"
                    @click.stop="$emit('retry', item.key)"
                >
                    {{ $t('routePlanning.multiDestination.progress.retry') }}
                </v-btn>
            </div>
            <div v-if="running && cancellable" class="roteirizacao-progresso-destinos__footer">
                <v-btn x-small text color="error" @click="$emit('cancel')">
                    {{ $t('routePlanning.multiDestination.progress.cancel') }}
                </v-btn>
            </div>
        </div>
    </v-card>
</template>

<script>
/**
 * Loading fixo e não bloqueante da roteirização: progresso geral e de cada destino no
 * multi-destino, ou a fase FINAL (aplicação das rotas e caminhadas) com um único destino.
 * Clicar num destino abre a página dele; o restante da tela segue utilizável.
 * Outras telas reaproveitam o mesmo painel passando `title`/`summaryText` (ex.: reprocessamento
 * da auditoria de viagens, uma linha por dia).
 */
export default {
    name: 'RoteirizacaoProgressoDestinos',
    props: {
        /** [{ key, label, status, statusIcon, statusColor, progress, message, active, retryable, busy?, indeterminate? }] */
        items: { type: Array, default: () => [] },
        /** { done, total, percent, indeterminate? } */
        overall: { type: Object, default: () => ({ done: 0, total: 0, percent: 0 }) },
        /** Algum pipeline ainda na fila ou calculando. */
        running: { type: Boolean, default: false },
        /** Mostra o "Cancelar" enquanto `running` (só quando há pipeline para cancelar). */
        cancellable: { type: Boolean, default: true },
        /** Roteirização com um único destino: sem contagem de destinos nem selo "na tela". */
        single: { type: Boolean, default: false },
        collapsed: { type: Boolean, default: false },
        /** Título no lugar do texto da roteirização. */
        title: { type: String, default: null },
        /** Resumo ao lado do título no lugar de "N de M destinos". */
        summaryText: { type: String, default: null },
    },
};
</script>

<style scoped>
.roteirizacao-progresso-destinos {
    position: fixed;
    left: 16px;
    bottom: 16px;
    z-index: 210;
    width: 340px;
    max-width: calc(100vw - 32px);
    font-size: 13px;
    overflow: hidden;
}
.roteirizacao-progresso-destinos__header {
    display: flex;
    align-items: center;
    padding-right: 6px;
}
.roteirizacao-progresso-destinos__toggle {
    display: flex;
    align-items: center;
    flex: 1 1 auto;
    min-width: 0;
    padding: 8px 10px;
    background: none;
    border: 0;
    color: inherit;
    cursor: pointer;
    text-align: left;
}
.roteirizacao-progresso-destinos__title {
    font-weight: 600;
    white-space: nowrap;
}
.roteirizacao-progresso-destinos__summary {
    margin-left: 8px;
    opacity: 0.75;
    white-space: nowrap;
}
.roteirizacao-progresso-destinos__body {
    max-height: 320px;
    overflow-y: auto;
    padding: 4px 0;
}
.roteirizacao-progresso-destinos__row {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 6px 12px;
}
.roteirizacao-progresso-destinos__row--clickable {
    cursor: pointer;
}
.roteirizacao-progresso-destinos__row--clickable:hover,
.roteirizacao-progresso-destinos__row--clickable:focus {
    background: rgba(25, 118, 210, 0.06);
    outline: none;
}
.roteirizacao-progresso-destinos__row--active {
    background: rgba(25, 118, 210, 0.1);
}
.roteirizacao-progresso-destinos__info {
    flex: 1 1 auto;
    min-width: 0;
}
.roteirizacao-progresso-destinos__name {
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.roteirizacao-progresso-destinos__badge {
    margin-left: 6px;
    padding: 0 6px;
    border-radius: 8px;
    background: #1976d2;
    color: #fff;
    font-size: 10px;
    font-weight: 600;
    vertical-align: middle;
}
.roteirizacao-progresso-destinos__message {
    font-size: 12px;
    opacity: 0.8;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.roteirizacao-progresso-destinos__pct {
    font-variant-numeric: tabular-nums;
    font-size: 12px;
    opacity: 0.8;
}
.roteirizacao-progresso-destinos__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 6px 12px 4px;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
}
</style>
